import { useState, useCallback, useRef } from 'react';

/**
 * The drag-and-drop *mechanics* for a file drop target: drag-over state and the
 * `onDragEnter`/`onDragOver`/`onDragLeave`/`onDrop` handlers (each calls
 * `preventDefault`). Spread `dropHandlers` onto any element. `FileDropZone`, the
 * job file area and the inspection photo slots all build on this so there is one
 * source of drag-drop truth.
 *
 * Validation/upload belong to the caller: `onFiles` receives the RAW dropped
 * files. When `disabled`, all handlers are no-ops.
 */
export interface UseFileDropOptions {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * Whether a drag is carrying files at all.
 *
 * Only a drag we can *positively* identify as something else is turned away:
 * `types` is absent on a hand-built test event, and an unknown drag is treated
 * as a file drag rather than silently doing nothing. What this rules out is the
 * page's own material — an image or a link dragged across a drop area should
 * not light it up as if it were an upload.
 */
function carriesFiles(e: React.DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return true;
  return Array.from(types).includes('Files');
}

export function useFileDrop({ onFiles, disabled = false }: UseFileDropOptions) {
  const [isDragOver, setIsDragOver] = useState(false);
  // How many elements inside the target the drag is currently inside of.
  //
  // `dragenter` and `dragleave` both bubble, so a target wrapped around content
  // — a grid of file cards, say — hears a leave every time the pointer crosses
  // from one card to the next. Counting rather than trusting a single leave is
  // what keeps the highlight steady: the new child's enter arrives before the
  // old child's leave, so the depth never touches zero on the way across.
  const depth = useRef(0);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    if (disabled) return;
    if (!carriesFiles(e)) return;
    e.preventDefault();
    depth.current += 1;
    setIsDragOver(true);
  }, [disabled]);

  // Also sets the state, not just `preventDefault`: a drag that began outside
  // the document — the usual case — can reach a target without its enter having
  // been seen, and `dragover` repeats for as long as the pointer is inside.
  const onDragOver = useCallback((e: React.DragEvent) => {
    if (disabled) return;
    if (!carriesFiles(e)) return;
    e.preventDefault();
    // Keep the count in step with the state it just forced, so the next leave
    // doesn't take it straight back to zero.
    if (depth.current === 0) depth.current = 1;
    setIsDragOver(true);
  }, [disabled]);

  const onDragLeave = useCallback(() => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setIsDragOver(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    if (disabled) return;
    if (!carriesFiles(e)) return;
    e.preventDefault();
    depth.current = 0;
    setIsDragOver(false);
    onFiles(Array.from(e.dataTransfer.files));
  }, [disabled, onFiles]);

  return { isDragOver, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
